/**
 * What the contact form asks, per topic. (BL-0096)
 *
 * The category is the first field and it drives the rest, because it is the
 * one answer that decides whether a report is actionable. "This document is
 * wrong" and "I cannot find this document" arrive as the same paragraph of
 * prose through a mailto and have to be told apart by hand; asked directly,
 * they sort themselves and carry the extra field each one needs.
 *
 * The ids here are the contract with `backend/.../services/contact.py`:
 * SUPPORT_CATEGORIES there is this list, and SUBCATEGORIES there is the
 * `subcategories` below. Changing one without the other is a 400.
 */

/** Extra fields a category reveals. `message` is always shown. */
export const FIELD = {
  DOC: 'document',
  QUERY: 'search_query',
};

export const CATEGORIES = [
  {
    id: 'document-error',
    label: 'Something in a document is wrong',
    blurb:
      'A date, a status, a number, a missing page, the wrong file attached — anything where this archive disagrees with the document itself.',
    fields: [FIELD.DOC],
    messageLabel: 'What is wrong, and what should it say?',
    messageHint:
      'If you can, quote what this page shows and what the document actually says.',
    subcategoryLabel: 'What kind of problem is it?',
    subcategories: [
      { id: 'metadata', label: 'Title, number or other details are wrong' },
      { id: 'file', label: 'The attached file is wrong, damaged or missing' },
      { id: 'status-or-dates', label: 'The status or the dates are wrong' },
      { id: 'relations', label: 'It links to the wrong document' },
      { id: 'missing-pages', label: 'Pages are missing from the file' },
      { id: 'other', label: 'Something else' },
    ],
  },
  {
    id: 'missing-document',
    label: 'A document is missing from this archive',
    blurb: 'You expected to find something here and it is not here.',
    fields: [FIELD.DOC],
    messageLabel: 'Which document, and where did you see it?',
    messageHint:
      'The document number if you have it, and a link to where it is published.',
    subcategories: [],
  },
  {
    id: 'site-bug',
    label: 'Something on this website is broken',
    blurb:
      'A page that will not load, a button that does nothing, a download that fails.',
    fields: [],
    messageLabel: 'What happened?',
    messageHint:
      'What you did, what happened, and what you expected instead. Screenshots help.',
    subcategories: [],
  },
  {
    id: 'search-problem',
    label: 'Search did not find what I was looking for',
    blurb: 'You searched, and the thing you wanted was not in the results.',
    fields: [FIELD.QUERY],
    messageLabel: 'What were you looking for?',
    messageHint:
      'Naming the document you expected is the most useful thing here.',
    subcategories: [],
  },
  {
    id: 'how-to',
    label: 'I have a question about using this archive',
    blurb:
      'How to find something, what a status means, how the versions relate.',
    fields: [],
    messageLabel: 'What would you like to know?',
    messageHint: '',
    subcategories: [],
  },
  {
    id: 'feature-request',
    label: 'I have an idea for this archive',
    blurb:
      'Something that would make this easier to search, read or cite. Tell us what you were trying to do.',
    fields: [],
    messageLabel: 'What would you like to be able to do?',
    messageHint:
      'What you were trying to do is more useful to us than the feature you have in mind.',
    subcategories: [],
  },
  {
    id: 'other',
    label: 'Something else',
    blurb: '',
    fields: [],
    messageLabel: 'Your message',
    messageHint: '',
    subcategories: [],
  },
];

export const CATEGORY_BY_ID = CATEGORIES.reduce((map, category) => {
  map[category.id] = category;
  return map;
}, {});

/**
 * A link into the contact form that arrives already knowing where it came
 * from. This is the whole provenance mechanism: `document.referrer` is
 * useless in a single-page app (it names the page the visitor first entered
 * on, not the view they were just looking at), and the public edge sends an
 * invalid `Referrer-Policy` header that degrades cross-origin referrers to a
 * bare origin anyway. So the link carries the context explicitly.
 *
 * EVERY CALLER MUST RENDER THIS WITH `rel="nofollow"`, and the reason is the
 * same property that makes the link useful. Carrying the context explicitly
 * means one distinct URL per document, so a crawler that follows these walks
 * the whole corpus into a form, and because each URL is unique not one of them
 * can be served from the edge cache -- every hit is a full server render.
 *
 * Measured 2026-09-02, four days after the form shipped: 6,682 of 131,818
 * requests to the three archives went to /contact, 4,432 cache MISS against 75
 * HIT, and only 212 requests in the entire retained edge log carried a referrer
 * from one of our own hosts -- an actual person clicking. ClaudeBot fetched it
 * 3,768 times out of its 22,140 total, making a contact form the single
 * most-crawled path on the site. Submissions in that window: zero.
 *
 * `Disallow: /contact?` in robots.txt (backend/scripts/seo/set_robots.py) is
 * the half that stops compliant crawlers fetching these. The nofollow is the
 * half that stops them being discovered in the first place, which is what
 * covers a crawler that ignores robots.txt. The bare /contact page is
 * deliberately left crawlable and index,follow by both. See BL-0113.
 *
 * @param {object} params
 * @param {string} [params.topic]  a CATEGORIES id
 * @param {string} [params.docid]  the document number, e.g. "DOE O 433.1B"
 * @param {string} [params.path]   the in-app path the visitor was on
 * @param {string} [params.q]      the search they had run
 */
export function contactHref({ topic, docid, path, q } = {}) {
  const params = new URLSearchParams();
  if (topic) params.set('topic', topic);
  if (docid) params.set('docid', docid);
  if (path) params.set('path', path);
  if (q) params.set('q', q);
  const query = params.toString();
  return query ? `/contact?${query}` : '/contact';
}
